import { FormField } from '@/components/ui/form-field'
import { FilterSelectAsync } from '@/components/ui/filters/filter-select-async'
import { AsyncFilterSelect } from '@/components/ui/filters/async-filter-select'
import { DEPARTMENT_IDS } from '@/constants/departments'
import {
    CUSTOMER_SOURCES,
    INDUSTRY_OPTIONS,
} from '@/modules/Customer/constants'
import LeadStatusLegend from '@/modules/Marketing/components/List/LeadFilters/LeadStatusLegend'
import { CustomerLeadTypes } from '@/modules/Marketing/types/utils'
import { getCompaniesOptions } from '@/modules/Company/services'
import {
    getEmployeeContains,
    getEmployeesOptionsPaginated,
} from '@/modules/Employee/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import { TFunction } from 'i18next'
import { Plus } from 'lucide-react'
import { FormItem } from 'vnetwork-ui'

interface StepBusinessInfoProps {
    control: any
    setValue: any
    customerType?: string
    customerSource?: string
    handleNoOptionCompany: (searchText?: string) => void
    handleCompanySelected: (option: any) => void
    t: TFunction
}

/**
 * Step 2: Business information
 */
const StepBusinessInfo = ({
    control,
    setValue,
    customerType,
    customerSource,
    handleNoOptionCompany,
    handleCompanySelected,
    t,
}: StepBusinessInfoProps) => (
    <div className="space-y-4">
        {/* radiogroup not supported by FormField — keep FormItem */}
        <div data-tour="customer-type">
            <FormItem
                type="radiogroup"
                control={control}
                name="customer.customer_type"
                options={[
                    {
                        label: t('individual-customers'),
                        value: 'individual',
                    },
                    {
                        label: t('business-customers'),
                        value: 'company',
                    },
                ]}
                direction="row"
                label={t('customer_type')}
                onChangeAfterHookForm={() => {
                    setValue('company.company_id', 0)
                }}
            />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
            {customerType === 'company' && (
                <div data-tour="company-select">
                    <FilterSelectAsync
                        control={control}
                        name="company.company_id"
                        label={`${t('company_tax_code')} *`}
                        placeholder={`${t('company_tax_code')}`}
                        searchPlaceholder={t('search_company_tax')}
                        emptyText={t('company_not_found')}
                        asyncOptions={getCompaniesOptions}
                        showClearButton={false}
                        onChangeAfterHookForm={handleCompanySelected}
                        footerItems={[
                            {
                                key: 'create-company',
                                label: t('create_new_company'),
                                icon: <Plus className="w-4 h-4 mr-1" />,
                                onClick: (setOpen, searchQuery) => {
                                    setOpen(false)
                                    handleNoOptionCompany(searchQuery)
                                },
                            },
                        ]}
                    />
                </div>
            )}

            <div data-tour="industry-select">
                <FormField
                    required
                    type="select"
                    control={control}
                    name="customer.customer_industry"
                    label={t('customer_industry')}
                    placeholder={t('customer_industry')}
                    options={INDUSTRY_OPTIONS}
                />
            </div>

            <div className="flex gap-4" data-tour="customer-source">
                <FormField
                    type="select"
                    control={control}
                    name="customer.customer_source"
                    label={t('customer_source')}
                    options={CUSTOMER_SOURCES(t)?.filter(
                        (item) => item.value !== 'marketing_approach'
                    )}
                    className="flex-1"
                    required
                    onAfterChangeValue={(_value, { setValue: set }) => {
                        if (customerSource === 'event') {
                            set('customer.customer_source_event', '')
                        }
                    }}
                />
                {(customerSource === 'event' ||
                    customerSource === 'sale_event') && (
                    <FormField
                        required
                        type="textfield"
                        control={control}
                        name="customer.customer_source_event"
                        label={t('event_name')}
                        placeholder={t('event_name')}
                        className="flex-1"
                    />
                )}
            </div>

            <FormField
                type="textfield"
                placeholder={t('customer_website')}
                control={control}
                name="customer.customer_website"
                label={t('customer_website')}
                className={customerType === 'company' ? '' : 'col-span-2'}
            />
        </div>
        <div data-tour="service-select">
            <AsyncFilterSelect
                control={control}
                name="customer.customer_service_interestings"
                label={t('customer_service_interestings')}
                placeholder={t('customer_service_interestings')}
                asyncOptions={async (query) => ({
                    options: await getProductsGroupOptions(query),
                    hasMore: false,
                })}
                multiple
                showClearButton={false}
            />
        </div>
        <AsyncFilterSelect
            control={control}
            name="customer.customer_sale_followers"
            label={t('sales-assignee')}
            placeholder={t('sales-assignee')}
            asyncOptions={(query, page) =>
                getEmployeesOptionsPaginated(
                    query,
                    [
                        DEPARTMENT_IDS.ACCOUNT,
                        DEPARTMENT_IDS.SALES,
                        DEPARTMENT_IDS.BOARD_OF_DIRECTORS,
                    ],
                    'active',
                    page
                )
            }
            getDefaultOptions={(values: number[]) =>
                getEmployeeContains({ employee_ids: values })
            }
            multiple
            showClearButton={false}
        />
        <FormField
            control={control}
            name="customer.customer_lead_type"
            type="select"
            label={t('customer-lead-type')}
            placeholder={t('customer-lead-type-placeholder')}
            options={CustomerLeadTypes(t)}
        />
        <LeadStatusLegend className="grid grid-cols-1 sm:grid-cols-1" />
    </div>
)

export default StepBusinessInfo
